<script setup lang="ts">
import ScoreChip from 'shared/components/ScoreChip/ScoreChip.vue';
import DateChip from 'shared/components/DateChip/DateChip.vue';
import { useDate } from 'shared/composables/useDate';
import type { StepCategory, StepTypeKebabCase } from 'types/course/step';
import { useIcon } from 'shared/composables/useIcon';
import {
  mdiCheckCircle,
  mdiLockOutline,
  mdiProgressCheck,
  mdiMinusCircleOutline,
  mdiTransitSkip,
} from '@quasar/extras/mdi-v7';

const { strapiDate } = useDate();
const { stepCategoryIcon, stepTypeIcon } = useIcon();

interface Props {
  stepName?: string;
  stepType?: StepTypeKebabCase;
  stepCategory?: StepCategory;
  stepScore?: number;
  startDate?: string | null;
  completionDate?: string | null;
  isSkipped?: boolean;
  active?: boolean;
  isIcon?: boolean;
  isStepType?: boolean;
  /**
   * Caption for rows the viewer may open even though a student would see them
   * locked (teacher preview's free navigation).
   */
  lockedHint?: string | null;
}

withDefaults(defineProps<Props>(), {
  stepName: '',
  stepType: undefined,
  stepCategory: undefined,
  stepScore: 0,
  startDate: null,
  completionDate: null,
  isSkipped: false,
  active: false,
  isIcon: false,
  isStepType: false,
  lockedHint: null,
});

// defineEmits<{
//   // 'update:modelValue': [payload: string]
// }>()
</script>

<template>
  <q-item
    v-ripple
    clickable
  >
    <q-item-section
      v-if="isIcon"
      avatar
    >
      <q-icon
        :name="stepCategoryIcon(stepCategory)"
        class="text-primary"
      />
    </q-item-section>

    <q-item-section>
      <q-item-label :class="{ 'text-h6': !$q.platform.is.mobile }">{{
        stepName
      }}</q-item-label>
      <q-item-label
        v-if="lockedHint"
        caption
        class="text-warning"
      >
        <q-icon
          :name="mdiLockOutline"
          size="14px"
          class="q-mr-xs"
        />{{ lockedHint }}
      </q-item-label>
      <q-item-label
        v-if="isStepType && stepType"
        caption
      >
        <q-chip
          :icon="stepTypeIcon(stepType as StepTypeKebabCase)"
          :color="active ? 'grey-8' : 'primary'"
          dense
          outline
          class="q-ma-none text-caption text-dark"
          :label="stepType"
        />
      </q-item-label>
    </q-item-section>

    <q-item-section
      v-if="stepType !== 'theory' && !isSkipped && !$q.platform.is.mobile"
      side
    >
      <ScoreChip
        color="secondary"
        dense
        size="lg"
      >
        {{ stepScore }}
      </ScoreChip>
    </q-item-section>

    <q-item-section
      v-if="!isSkipped"
      side
    >
      <q-item-label
        caption
        class="text-weight-thin text-primary q-pr-xs"
      >
        {{ $t('START_DATE') }}
      </q-item-label>
      <q-item-label caption>
        <DateChip
          dense
          square
          color="accent"
          class="q-ma-none text-caption text-dark"
          >{{ strapiDate(startDate ?? '', 'short') }}</DateChip
        >
      </q-item-label>
      <q-item-label
        v-if="stepType !== 'theory' && !isSkipped && $q.platform.is.mobile"
      >
        <ScoreChip
          color="secondary"
          dense
          size="md"
        >
          {{ stepScore }}
        </ScoreChip>
      </q-item-label>
    </q-item-section>
    <q-item-section
      v-else
      side
    >
      <q-item-label
        caption
        class="text-weight-thin text-primary"
      >
        {{ $t('SKIPPED') }}
      </q-item-label>
      <q-item-label caption>
        {{ $t('YOU_DID_GOOD_ON_PREVIOUS_STEPS') }}
      </q-item-label>
    </q-item-section>

    <q-item-section side>
      <q-icon
        :name="
          isSkipped
            ? mdiTransitSkip
            : completionDate
              ? mdiCheckCircle
              : startDate
                ? mdiProgressCheck
                : mdiMinusCircleOutline
        "
        :color="
          isSkipped
            ? 'warning'
            : completionDate
              ? 'positive'
              : startDate
                ? 'warning'
                : 'negative'
        "
      />
    </q-item-section>
  </q-item>
</template>
